import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdminToStoreMessage,
  CMS_PROTOCOL_VERSION,
  CmsEditableKey,
  CmsFieldType,
  DistributiveOmit,
  StoreToAdminMessage,
  isCmsMessage,
} from './protocol';

/**
 * CMS Edit Mode runtime (storefront side).
 *
 * Dormant for customers. Activates ONLY when all of the following hold:
 *   1. the page was opened with ?cms_edit=1 (remembered in sessionStorage so
 *      in-app navigation keeps the mode on for the tab),
 *   2. the page is inside an iframe (window.parent !== window),
 *   3. the parent completes a `cms:hello` handshake from an allowed origin.
 *
 * It never writes to the API. It only (a) overlays draft values the admin
 * pushes so the page previews edits live, and (b) reports which CMS keys are
 * on screen / which one the user clicked. All persistence happens in the
 * admin app with the admin's credentials.
 */

const SESSION_FLAG = 'cms_edit';

const DEFAULT_ALLOWED_ORIGINS = 'https://*.bodhisys.io,http://localhost:*,http://127.0.0.1:*';

const originMatches = (origin: string, pattern: string): boolean => {
  const p = pattern.trim();
  if (!p) return false;
  if (p === origin) return true;
  // Wildcard support: scheme://*.host[:port] and scheme://host:*
  const re = new RegExp('^' + p
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '[^/]*') + '$');
  return re.test(origin);
};

const isAllowedOrigin = (origin: string): boolean => {
  const list = (process.env.REACT_APP_CMS_ADMIN_ORIGINS || DEFAULT_ALLOWED_ORIGINS).split(',');
  return list.some(p => originMatches(origin, p));
};

const detectRequested = (): boolean => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cms_edit') === '1') {
      sessionStorage.setItem(SESSION_FLAG, '1');
      return true;
    }
    if (params.get('cms_edit') === '0') {
      sessionStorage.removeItem(SESSION_FLAG);
      return false;
    }
    return sessionStorage.getItem(SESSION_FLAG) === '1';
  } catch {
    return false;
  }
};

const inIframe = (): boolean => {
  try { return window.parent !== window; } catch { return true; }
};

export interface CmsEditContextProps {
  /** Requested via URL and inside an iframe; handshake may still be pending. */
  requested: boolean;
  /** Fully active: handshake completed with an allowed admin origin. */
  enabled: boolean;
  draft: Record<string, string>;
  selectedKey: string | null;
  highlightAll: boolean;
  select: (key: string, fieldType: CmsFieldType, value: string, el: HTMLElement | null) => void;
  registerKey: (key: string, fieldType: CmsFieldType, label?: string) => () => void;
  /** Called by CmsRouterBridge (inside <Router>) so we can navigate + report routes. */
  attachRouter: (navigate: (path: string) => void, route: string) => void;
  reportRoute: (route: string) => void;
}

const noop = () => {};
export const CmsEditContext = createContext<CmsEditContextProps>({
  requested: false,
  enabled: false,
  draft: {},
  selectedKey: null,
  highlightAll: false,
  select: noop,
  registerKey: () => noop,
  attachRouter: noop,
  reportRoute: noop,
});

export const useCmsEdit = () => useContext(CmsEditContext);

export const CmsEditProvider = ({ children }: { children: React.ReactNode }) => {
  const requested = useMemo(() => detectRequested() && inIframe(), []);
  const [adminOrigin, setAdminOrigin] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [highlightAll, setHighlightAll] = useState(false);

  const keysRef = useRef<Map<string, CmsEditableKey>>(new Map());
  const routeRef = useRef<string>(window.location.pathname);
  const navigateRef = useRef<((path: string) => void) | null>(null);
  const keysTimer = useRef<number | null>(null);

  const enabled = requested && !!adminOrigin;

  const post = useCallback((msg: DistributiveOmit<StoreToAdminMessage, 'v'>) => {
    if (!adminOrigin) return;
    try {
      window.parent.postMessage({ v: CMS_PROTOCOL_VERSION, ...msg }, adminOrigin);
    } catch { /* ignore */ }
  }, [adminOrigin]);

  const currentKeys = () => Array.from(keysRef.current.values());

  // Debounced "these keys are on screen now" report.
  const scheduleKeysReport = useCallback(() => {
    if (!adminOrigin) return;
    if (keysTimer.current) window.clearTimeout(keysTimer.current);
    keysTimer.current = window.setTimeout(() => {
      post({ type: 'cms:keys', route: routeRef.current, keys: currentKeys() });
    }, 120);
  }, [adminOrigin, post]);

  // Handshake + command listener.
  useEffect(() => {
    if (!requested) return;
    const onMessage = (event: MessageEvent) => {
      if (!isCmsMessage(event.data)) return;
      if (!isAllowedOrigin(event.origin)) return;
      const msg = event.data as AdminToStoreMessage;

      if (msg.type === 'cms:hello') {
        setAdminOrigin(event.origin);
        window.parent.postMessage(
          { v: CMS_PROTOCOL_VERSION, type: 'cms:ready', route: routeRef.current, keys: currentKeys() },
          event.origin,
        );
        return;
      }
      // Every other command requires a completed handshake from the same origin.
      if (!adminOrigin || event.origin !== adminOrigin) return;

      switch (msg.type) {
        case 'cms:apply':
          setDraft(prev => ({ ...prev, [msg.key]: msg.value }));
          break;
        case 'cms:clearDraft':
          setDraft(prev => {
            if (!msg.keys) return {};
            const next = { ...prev };
            msg.keys.forEach(k => delete next[k]);
            return next;
          });
          break;
        case 'cms:navigate':
          if (navigateRef.current) navigateRef.current(msg.path);
          else window.location.assign(msg.path);
          break;
        case 'cms:highlightAll':
          setHighlightAll(!!msg.on);
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [requested, adminOrigin]);

  const select = useCallback((key: string, fieldType: CmsFieldType, value: string, el: HTMLElement | null) => {
    setSelectedKey(key);
    const r = el?.getBoundingClientRect();
    post({
      type: 'cms:selected',
      key,
      fieldType,
      value,
      rect: r ? { top: r.top, left: r.left, width: r.width, height: r.height } : { top: 0, left: 0, width: 0, height: 0 },
    });
  }, [post]);

  const registerKey = useCallback((key: string, fieldType: CmsFieldType, label?: string) => {
    keysRef.current.set(key, { key, fieldType, label });
    scheduleKeysReport();
    return () => {
      keysRef.current.delete(key);
      scheduleKeysReport();
    };
  }, [scheduleKeysReport]);

  const reportRoute = useCallback((route: string) => {
    routeRef.current = route;
    post({ type: 'cms:route', route });
    scheduleKeysReport();
  }, [post, scheduleKeysReport]);

  const attachRouter = useCallback((navigate: (path: string) => void, route: string) => {
    navigateRef.current = navigate;
    routeRef.current = route;
  }, []);

  const value = useMemo<CmsEditContextProps>(() => ({
    requested, enabled, draft, selectedKey, highlightAll, select, registerKey, attachRouter, reportRoute,
  }), [requested, enabled, draft, selectedKey, highlightAll, select, registerKey, attachRouter, reportRoute]);

  return <CmsEditContext.Provider value={value}>{children}</CmsEditContext.Provider>;
};
